// ComponentRenderer.js - Bridge between Handlebars and React components
import React from 'react';
import { createRoot } from 'react-dom/client';
import FeaturedCarousel from './FeaturedCarousel.jsx';
import NavigationComponent from './Navigation.jsx';
import Footer from './Footer.jsx';
import PotredPathDrawing from './PotredPathDrawing.jsx';
import PageLoadingProgressMotion from './PageLoadingProgressMotion.jsx';
import AnimatedBackgroundReact from './AnimatedBackgroundReact.jsx';
import ScrollProgressWheel from './ScrollProgressWheel.jsx';
import TagSortingIcon from './TagSortingIcon.jsx';
import TTSPlayer from './TTSPlayer.jsx';

class ComponentRenderer {
  constructor() {
    this.roots = new Map();
  }

  // Initialize all components when DOM is ready - CLIENT-SIDE ONLY
  init() {
    // Only render on client-side to avoid server-side rendering issues
    if (typeof window === 'undefined') return;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderAll());
    } else {
      this.renderAll();
    }
  }

  // Initialize components without animated background (for loading sequence) - CLIENT-SIDE ONLY
  initWithoutBackground() {
    // Only render on client-side to avoid server-side rendering issues
    if (typeof window === 'undefined') return;
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderAllExceptBackground());
    } else {
      this.renderAllExceptBackground();
    }
  }

  renderAll() {
    this.renderNavigation();
    this.renderFeaturedCarousel();
    this.renderFooter();
    this.renderAnimatedBackground();
    // ScrollProgressWheel only renders on blog post pages (with mount point)
    this.renderScrollProgressWheel();
    // TagSortingIcon only renders on tag pages
    this.renderTagSortingIcon();
    // TTSPlayer only renders on blog post pages
    this.renderTTSPlayer();
  }

  renderAllExceptBackground() {
    this.renderNavigation();
    this.renderFeaturedCarousel();
    this.renderFooter();
    // ScrollProgressWheel only renders on blog post pages (with mount point)
    this.renderScrollProgressWheel();
    // TagSortingIcon only renders on tag pages
    this.renderTagSortingIcon();
    // TTSPlayer only renders on blog post pages
    this.renderTTSPlayer();
    // Animated background will be rendered after loading completes
  }

  renderNavigation() {
    const navElement = document.getElementById('potred-navigation-mount');
    if (!navElement) return;

    try {
      const props = this.parseDataAttributes(navElement);
      const root = createRoot(navElement);
      root.render(React.createElement(NavigationComponent, props));
      this.roots.set('navigation', root);
    } catch (error) {
      console.error('Error rendering navigation:', error);
    }
  }

  renderFeaturedCarousel() {
    const carouselElement = document.getElementById('potred-carousel-mount');
    if (!carouselElement) return;

    try {
      // Get data from pipe-delimited script tag or legacy JSON/data-posts
      const base64DataScript = document.getElementById('carousel-data-base64');
      const dataScript = document.getElementById('carousel-data');
      let posts = [];
      
      if (base64DataScript) {
        try {
          // Parse pipe-delimited data format (safer for Chinese characters)
          const rawData = base64DataScript.textContent?.trim() || '';
          if (rawData) {
            const postEntries = rawData.split('||');
            posts = postEntries.map(entry => {
              const [id, title, excerpt, url, feature_image, published_at, reading_time, tag_name, tag_slug, tag_color] = entry.split('|');
              return {
                id: id || '',
                title: title || '',
                excerpt: excerpt || '',
                url: url || '',
                feature_image: feature_image === 'null' ? null : feature_image,
                published_at: published_at || '',
                reading_time: parseInt(reading_time) || 5,
                primary_tag: tag_name === 'null' ? null : { name: tag_name, slug: tag_slug, accent_color: tag_color || '#ea580c' }
              };
            }).filter(post => post.id); // Filter out empty entries
            
            console.log('✅ Carousel data loaded from pipe-delimited format:', posts.length, 'posts');
          }
        } catch (parseError) {
          console.warn('Failed to parse pipe-delimited carousel data:', parseError);
          posts = [];
        }
      } else if (dataScript) {
        try {
          // Enhanced JSON parsing with Unicode character handling
          let jsonContent = dataScript.textContent || '[]';
          
          // Clean and normalize the JSON string for Chinese characters
          jsonContent = jsonContent
            .replace(/\r?\n/g, ' ')                    // Replace line breaks
            .replace(/\s+/g, ' ')                      // Normalize spaces
            .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ') // Remove control chars but preserve Chinese
            .trim();
          
          posts = JSON.parse(jsonContent);
          console.log('✅ Carousel data loaded from script tag:', posts.length, 'posts');
        } catch (parseError) {
          console.warn('Failed to parse carousel data from script:', parseError);
          // Try alternative parsing approach for Chinese characters
          try {
            let rawContent = dataScript.textContent || '[]';
            // More aggressive cleaning while preserving Unicode
            rawContent = rawContent
              .replace(/[\r\n\t]/g, ' ')               // Remove line breaks and tabs
              .replace(/\s{2,}/g, ' ')                 // Multiple spaces to single
              .replace(/\u0000-\u001F/g, '')          // Remove control characters
              .replace(/\u007F-\u009F/g, '')          // Remove DEL and C1 controls
              .trim();
            
            posts = JSON.parse(rawContent);
            console.log('✅ Carousel data parsed with fallback method:', posts.length, 'posts');
          } catch (secondError) {
            console.error('❌ Both JSON parsing attempts failed:', secondError);
            posts = [];
          }
        }
      } else if (carouselElement.hasAttribute('data-posts')) {
        // Fallback: try to get data from data-posts attribute
        try {
          let postsData = carouselElement.getAttribute('data-posts');
          // Clean the JSON string by normalizing whitespace and removing control characters
          postsData = postsData
            .replace(/\r?\n/g, ' ')           // Replace line breaks with spaces
            .replace(/\s+/g, ' ')             // Normalize multiple spaces to single space
            .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ') // Remove control characters
            .trim();                          // Remove leading/trailing whitespace
          
          posts = JSON.parse(postsData || '[]');
          console.log('✅ Carousel data loaded from data-posts attribute:', posts.length, 'posts');
        } catch (parseError) {
          console.warn('Failed to parse carousel data from data-posts attribute:', parseError);
          posts = [];
        }
      }
      
      if (posts.length > 0) {
        const props = { posts };
        const root = createRoot(carouselElement);
        root.render(React.createElement(FeaturedCarousel, props));
        this.roots.set('carousel', root);
        console.log('✅ FeaturedCarousel component rendered successfully');
      } else {
        console.warn('⚠️ No carousel posts data found - carousel not rendered');
      }
    } catch (error) {
      console.error('❌ Error rendering carousel:', error);
    }
  }

  renderFooter() {
    const footerElement = document.getElementById('potred-footer-mount');
    if (!footerElement) return;

    try {
      const props = this.parseDataAttributes(footerElement);
      const root = createRoot(footerElement);
      root.render(React.createElement(Footer, props));
      this.roots.set('footer', root);
      console.log('✅ Footer component rendered successfully');
    } catch (error) {
      console.error('❌ Error rendering footer:', error);
    }
  }

  renderAnimatedBackground() {
    const backgroundElement = document.getElementById('potred-animated-background-mount');
    if (!backgroundElement) return;

    try {
      const root = createRoot(backgroundElement);
      root.render(React.createElement(AnimatedBackgroundReact));
      this.roots.set('animatedBackground', root);
      console.log('✅ AnimatedBackground component rendered successfully');
    } catch (error) {
      console.error('❌ Error rendering animated background:', error);
    }
  }

  renderScrollProgressWheel() {
    // Only render on blog post pages (look for specific mount point)
    const scrollElement = document.getElementById('scroll-progress-wheel-mount');
    if (!scrollElement) {
      console.log('🔍 ScrollProgressWheel mount point not found - not a blog post page');
      return;
    }

    try {
      const root = createRoot(scrollElement);
      root.render(React.createElement(ScrollProgressWheel));
      this.roots.set('scrollProgress', root);
      console.log('✅ ScrollProgressWheel component rendered successfully on blog post page');
    } catch (error) {
      console.error('❌ Error rendering scroll progress wheel:', error);
    }
  }

  renderTagSortingIcon() {
    // Only render on tag pages (look for specific mount point)
    const sortElement = document.getElementById('tag-sorting-mount');
    if (!sortElement) {
      console.log('🔍 TagSortingIcon mount point not found - not a tag page');
      return;
    }

    try {
      const onSort = (direction) => {
        console.log('🔄 Sorting posts by date:', direction);
        // In a real implementation, you would reorder the posts on the page
        // For now, we'll just show a visual feedback
        const postsGrid = document.getElementById('posts-grid');
        if (postsGrid) {
          const cards = Array.from(postsGrid.children);
          if (direction === 'asc') {
            // Show visual feedback that posts are "reordering"
            postsGrid.style.opacity = '0.7';
            setTimeout(() => {
              postsGrid.style.opacity = '1';
            }, 300);
          } else {
            // Default order (newest first)
            postsGrid.style.opacity = '0.7';
            setTimeout(() => {
              postsGrid.style.opacity = '1';
            }, 300);
          }
        }
      };

      const props = { onSort };
      const root = createRoot(sortElement);
      root.render(React.createElement(TagSortingIcon, props));
      this.roots.set('tagSorting', root);
      console.log('✅ TagSortingIcon component rendered successfully on tag page');
    } catch (error) {
      console.error('❌ Error rendering tag sorting icon:', error);
    }
  }

  renderTTSPlayer() {
    // Check if GPU_TTS feature is enabled
    const gpuTtsEnabled = window.POTRED_THEME_DATA?.features?.gpuTts || false;
    if (!gpuTtsEnabled) {
      console.log('🔍 TTS feature disabled (GPU_TTS=false) - skipping TTS player');
      return;
    }

    // Only render on blog post pages (look for specific mount point)
    const ttsElement = document.getElementById('tts-player-mount');
    if (!ttsElement) {
      console.log('🔍 TTS Player mount point not found - not a blog post page');
      return;
    }

    try {
      // Get article content for TTS
      const articleContent = document.querySelector('#blog-post-article-content article');
      if (!articleContent) {
        console.warn('⚠️ Article content not found - TTS player not rendered');
        return;
      }

      // Get props from data attributes
      const postTitle = ttsElement.dataset.postTitle || 'Blog Post';
      const language = ttsElement.dataset.language || 'zh';
      const content = articleContent.innerHTML;

      const props = {
        content,
        postTitle,
        language
      };

      // Make mount point visible
      ttsElement.style.display = 'block';

      const root = createRoot(ttsElement);
      root.render(React.createElement(TTSPlayer, props));
      this.roots.set('ttsPlayer', root);
      console.log('✅ TTS Player component rendered successfully on blog post page');
    } catch (error) {
      console.error('❌ Error rendering TTS player:', error);
    }
  }

  // Parse data attributes from HTML element
  parseDataAttributes(element) {
    const data = {};
    
    // Get all data-* attributes
    for (const [key, value] of Object.entries(element.dataset)) {
      const camelKey = this.camelCase(key);
      
      // Only try to parse arrays and objects as JSON
      if (key === 'posts' || key === 'navigation' || key === 'ghostPages' || key === 'siteMaintenancePages' || key === 'categories') {
        try {
          // Clean the JSON string by replacing problematic characters
          let cleanValue = value;
          // Replace line breaks and normalize spaces
          cleanValue = cleanValue.replace(/\r?\n/g, ' ');
          cleanValue = cleanValue.replace(/\s+/g, ' ');
          
          data[camelKey] = JSON.parse(cleanValue);
        } catch (error) {
          console.warn(`Failed to parse data attribute ${key}:`, error);
          // Try a more aggressive cleanup
          try {
            let aggressiveClean = value
              .replace(/\r?\n/g, ' ')
              .replace(/\s+/g, ' ')
              .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ') // Remove control characters
              .trim();
            data[camelKey] = JSON.parse(aggressiveClean);
          } catch (secondError) {
            console.warn(`Second parse attempt failed for ${key}:`, secondError);
            data[camelKey] = key === 'posts' ? [] : [];
          }
        }
      } else if (key === 'searchEnabled' || key === 'membersEnabled') {
        // Parse boolean values
        data[camelKey] = value === 'true';
      } else {
        // Use as string for simple values
        data[camelKey] = value;
      }
    }
    
    // Ensure posts is always an array for carousel
    if (data.posts && !Array.isArray(data.posts)) {
      data.posts = [];
    }
    
    console.log('Parsed component data:', data);
    return data;
  }

  // Convert kebab-case to camelCase
  camelCase(str) {
    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
  }

  // Clean up when needed
  destroy() {
    this.roots.forEach(root => {
      try {
        root.unmount();
      } catch (error) {
        console.warn('Error unmounting component:', error);
      }
    });
    this.roots.clear();
  }
}

// Global instance
window.PotredComponents = new ComponentRenderer();

export default ComponentRenderer;